import Link from '@mui/material/Link';
import { Box, Stack, Avatar, Tooltip, Typography } from '@mui/material';

import { RouterLink } from 'src/routes/components';

import { truncateText } from 'src/pages/chat/utils/text-formatting';

// ----------------------------------------------------------------------

type RowCellNameProps = {
  row: any;
  value: string;
  headCellId: string;
  editHref: (id: string) => string;
  isSelectRecordDialog?: boolean;
  isSupportReport?: boolean;
  moduleName?: string;
  isTenantInvoice?: boolean;
  tenantInvoiceDetailsHref?: (tenantId: string, userId: string) => string;
  currentTenantId?: string;
  handleEditContact?: (id: string) => string | Promise<void>;
};

// ----------------------------------------------------------------------

export function RowCellName({
  row,
  value,
  headCellId,
  editHref,
  isSelectRecordDialog = false,
  isSupportReport = false,
  moduleName = '',
  isTenantInvoice = false,
  tenantInvoiceDetailsHref,
  currentTenantId,
  handleEditContact,
}: RowCellNameProps) {
  const updatedValue =
    value && value.length > 20 ? (
      <Tooltip title={value}>
        <Typography component="span" sx={{ fontSize: 'inherit' }}>
          {truncateText(value, 20)}
        </Typography>
      </Tooltip>
    ) : (
      value
    );

  const firstChar =
    'name' in row
      ? row.name
      : 'firstName' in row
        ? row.firstName || row.lastName
        : 'teamName' in row
          ? row.teamName
          : undefined;

  const getHref = () => {
    if (!('id' in row)) return '#';

    if (moduleName === 'bill' && isTenantInvoice && tenantInvoiceDetailsHref && currentTenantId) {
      return tenantInvoiceDetailsHref(currentTenantId, row.id);
    }

    const href = editHref(row.id);
    if (!href || href === '#') return undefined;

    return href;
  };

  const handleClick = (e: React.MouseEvent) => {
    if (handleEditContact && 'id' in row) {
      const href = getHref();
      if (!href) {
        e.preventDefault();
        handleEditContact(row.id);
      }
    }
  };

  return (
    <Box sx={{ gap: 2, display: 'flex', alignItems: 'center' }}>
      {'ProfileURL' in row && headCellId !== 'email' && headCellId !== 'lastName' && (
        <Avatar
          alt={firstChar}
          src={'ProfileURL' in row ? row.ProfileURL || undefined : undefined}
          sx={{
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              transform: 'scale(1.1)',
              boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
            },
          }}
        >
          {firstChar?.charAt(0).toUpperCase()}
        </Avatar>
      )}
      <Stack sx={{ typography: 'body2', flex: '1 1 auto', alignItems: 'flex-start' }}>
        {isSelectRecordDialog ||
        isSupportReport ||
        (moduleName === 'CAMPAIGN' &&
          'campaignStatus' in row &&
          row.campaignStatus !== 'completed') ||
        moduleName === 'SNIPPET' ||
        moduleName === 'WEBHOOK' ? (
          <Box
            sx={{
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                color: 'primary.main',
              },
            }}
          >
            {updatedValue}
          </Box>
        ) : (
          <Link
            {...(getHref() ? { component: RouterLink, href: getHref() } : { component: 'span' })}
            onClick={handleClick}
            color="inherit"
            sx={{
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              fontWeight: 600,
              textDecoration: 'none',
              '&:hover': {
                color: 'primary.main',
                textDecoration: 'underline',
              },
            }}
          >
            {updatedValue}
          </Link>
        )}
      </Stack>
    </Box>
  );
}
